import React, { useEffect, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import useTableOptions from "Hooks/useTableOptions";
import CommonChip from "Components/UI/Tables/CommonChip";
import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";
import { useAccountCharts } from "Context/AccountCharts";

const createRows = (articles, accountCharts) => {
  return articles.map((a) => {
    const debetAccount = accountCharts.find(
      (ac) => Number(ac.Id) === Number(a.DebitAccountChartId)
    );
    const creditAccount = accountCharts.find(
      (ac) => Number(ac.Id) === Number(a.CreditAccountChartId)
    );
    return [
      a.Id,
      a.Title,
      a.Name,
      {
        name: debetAccount
          ? `${debetAccount.AccountNumber} - ${debetAccount.Name}`
          : "",
        id: debetAccount ? debetAccount.Id : 0,
      },
      {
        name: creditAccount
          ? `${creditAccount.AccountNumber} - ${creditAccount.Name}`
          : "",
        id: creditAccount ? creditAccount.Id : 0,
      },
      a.VAT,
      a.Payrule,
      a.Paytype,
      getParseStatusLabelById(a.ParseStatus),
      cutStrAtAndAddDots(a.ParseMessage, 45),
      getSystemStatusLabelById(a.SystemStatus),
      a.CreateTime,
    ];
  });
};

export default function ClientArticlesTableContainer({ articles }) {
  const { handleColumnShow, isColDisplayed } = useTableOptions(
    "ClientArticlesMainTbl"
  );
  const [rows, setRows] = useState([]);
  const { fetchAccountCharts, accountCharts } = useAccountCharts();

  useEffect(() => {
    if (!accountCharts || accountCharts.length === 0) {
      fetchAccountCharts();
    }

    const rows = createRows(articles, accountCharts);
    setRows(rows);
  }, [accountCharts]);

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => {
          return (
            <IconButton
              component={Link}
              to={`${rows[dataIndex][0]}`}
              aria-label="edit"
              size="small"
            >
              <LinkIcon sx={{ p: 0, m: 0 }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "ID",
      options: {
        filter: false,
        display: isColDisplayed("KlientArtikel ID"),
      },
    },
    {
      name: "Titel",
      options: {
        filter: false,
        display: isColDisplayed("Titel"),
      },
    },
    {
      name: "Namn",
      options: {
        filter: false,
        display: isColDisplayed("Namn"),
      },
    },
    {
      name: "Debet konto",
      options: {
        filter: false,
        display: isColDisplayed("Debet konto"),
        customBodyRender: (value) => {
          return <Link to={`/accountcharts/${value.id}`}>{value.name}</Link>;
        },
      },
    },
    {
      name: "Kredit konto",
      options: {
        filter: false,
        display: isColDisplayed("Kredit konto"),
        customBodyRender: (value) => {
          return <Link to={`/accountcharts/${value.id}`}>{value.name}</Link>;
        },
      },
    },
    {
      name: "Moms (VAT)",
      options: {
        filter: false,
        display: isColDisplayed("Moms (VAT)"),
      },
    },
    {
      name: "Betalregel",
      options: {
        // filter: false,
        display: isColDisplayed("Betalregel"),
      },
    },
    {
      name: "Betaltyp",
      options: {
        display: isColDisplayed("Betaltyp"),
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        filter: true,
        display: isColDisplayed("Kontrollstatus"),
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        filter: false,
        display: isColDisplayed("Kontrollmeddelande"),
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        display: isColDisplayed("Status"),
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        filter: false,
        display: isColDisplayed("Skapad"),
      },
    },
  ];

  const options = {
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    customToolbar: () => {
      return (
        <>
          <RefreshBtn />
        </>
      );
    },
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    responsive: "standard",
    downloadOptions: {
      separator: ";",
      filename: "client_accounts.csv",
    },
    rowsPerPage: 30,
    rowsPerPageOptions: [10, 30, 100],
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  return (
    <MUITable options={options} title={""} data={rows} columns={columns} />
  );
}
